import { useState, useCallback, useMemo, useEffect } from 'react';

export interface ColumnPrefs {
    /** Order of column keys (visible and hidden); hidden keys still appear in order for “show again” UX */
    order: string[];
    hidden: string[];
}

const defaultPrefs = (allKeys: string[], defaultHiddenKeys: string[]): ColumnPrefs => {
    const hidden = defaultHiddenKeys.filter((k) => allKeys.includes(k));
    return { order: [...allKeys], hidden };
};

const loadPrefs = (
    storageKey: string,
    fallback: string[],
    defaultHiddenKeys: string[],
    keyRenames: Record<string, string> = {},
): ColumnPrefs => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return defaultPrefs(fallback, defaultHiddenKeys);
        const parsed = JSON.parse(raw) as Partial<ColumnPrefs>;
        if (!Array.isArray(parsed.order)) return defaultPrefs(fallback, defaultHiddenKeys);
        const remap = (k: string) => keyRenames[k] ?? k;
        const prevOrderArr = Array.isArray(parsed.order) ? parsed.order.map(remap) : [];
        const order = mergeOrderWithFallback(prevOrderArr, fallback);
        const prevKeys = new Set(prevOrderArr);
        const hiddenSet = new Set(
            Array.isArray(parsed.hidden)
                ? parsed.hidden.map(remap).filter((k) => fallback.includes(k))
                : []
        );
        for (const k of fallback) {
            if (!prevKeys.has(k) && defaultHiddenKeys.includes(k)) {
                hiddenSet.add(k);
            }
        }
        return { order, hidden: Array.from(hiddenSet) };
    } catch {
        return defaultPrefs(fallback, defaultHiddenKeys);
    }
};

/** Keep saved order; append any new keys from fallback at end */
function mergeOrderWithFallback(savedOrder: string[], fallback: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const k of savedOrder) {
        if (fallback.includes(k) && !seen.has(k)) {
            out.push(k);
            seen.add(k);
        }
    }
    for (const k of fallback) {
        if (!seen.has(k)) {
            out.push(k);
            seen.add(k);
        }
    }
    return out;
}

const savePrefs = (storageKey: string, prefs: ColumnPrefs) => {
    try {
        localStorage.setItem(storageKey, JSON.stringify(prefs));
    } catch {
        // ignore
    }
};

/**
 * Column visibility + order for reporting tables. `pinnedKeys` stay first (fixed order) and cannot be hidden.
 */
export function useReportingColumnPrefs(
    storageKey: string,
    allKeys: string[],
    pinnedKeys: string[] = [],
    /** Keys that start hidden until the user enables them (e.g. newly added columns). */
    defaultHiddenKeys: string[] = [],
    /** Map old persisted keys → new keys so renames keep position instead of appending. */
    keyRenames: Record<string, string> = {},
) {
    const fallbackKey = allKeys.join('|');
    const defaultHiddenKey = defaultHiddenKeys.join('|');
    const renamesKey = JSON.stringify(keyRenames);

    const [prefs, setPrefs] = useState<ColumnPrefs>(() =>
        loadPrefs(storageKey, allKeys, defaultHiddenKeys, keyRenames)
    );

    useEffect(() => {
        setPrefs(loadPrefs(storageKey, allKeys, defaultHiddenKeys, keyRenames));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when key set changes
    }, [storageKey, fallbackKey, defaultHiddenKey, renamesKey]);

    const setAndPersist = useCallback(
        (next: ColumnPrefs) => {
            savePrefs(storageKey, next);
            setPrefs(next);
        },
        [storageKey]
    );

    const toggleVisible = useCallback(
        (key: string) => {
            if (pinnedKeys.includes(key)) return;
            setAndPersist({
                ...prefs,
                hidden: prefs.hidden.includes(key)
                    ? prefs.hidden.filter((k) => k !== key)
                    : [...prefs.hidden, key],
            });
        },
        [pinnedKeys, prefs, setAndPersist]
    );

    /** Move one column before another in the order (both must be unpinned). */
    const reorderByKey = useCallback(
        (fromKey: string, toKey: string) => {
            if (fromKey === toKey) return;
            if (pinnedKeys.includes(fromKey) || pinnedKeys.includes(toKey)) return;
            const order = [...prefs.order];
            const fi = order.indexOf(fromKey);
            const ti = order.indexOf(toKey);
            if (fi < 0 || ti < 0) return;
            order.splice(fi, 1);
            const newTi = order.indexOf(toKey);
            order.splice(newTi, 0, fromKey);
            setAndPersist({ ...prefs, order });
        },
        [pinnedKeys, prefs, setAndPersist]
    );

    const reset = useCallback(() => {
        setAndPersist(defaultPrefs(allKeys, defaultHiddenKeys));
    }, [allKeys, defaultHiddenKeys, setAndPersist]);

    const visibleOrderedKeys = useMemo(() => {
        return prefs.order.filter((k) => allKeys.includes(k) && !prefs.hidden.includes(k));
    }, [allKeys, prefs.hidden, prefs.order]);

    const columnConfigForDrawer = useMemo(() => {
        return prefs.order
            .filter((k) => allKeys.includes(k))
            .map((key) => ({
                key,
                visible: !prefs.hidden.includes(key),
                pinned: pinnedKeys.includes(key),
            }));
    }, [allKeys, prefs.hidden, prefs.order, pinnedKeys]);

    return {
        prefs,
        visibleOrderedKeys,
        columnConfigForDrawer,
        toggleVisible,
        reorderByKey,
        reset,
        setPrefs: setAndPersist,
    };
}
